import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";
import { processBillingUnitFormSchema } from "../utils/billingUnitFormRules";

/**
 * Builds BillingUnitFormModal's field-schema sections from the org's
 * active "billingUnit" object customizations, resolving any dynamic
 * (external reference) fields' data along the way - mirrors
 * _features/markets/modals/MarketFormModal/hooks/useMarketFormSchema.js
 * exactly (Billing Units is the dynamic-form engine's 3rd consumer, after
 * Company and Markets). Unlike Markets' own post-processing (which has to
 * run before resolution, to retype a reference-driven field before
 * useResolveFormSchema fetches its data), none of this file's rules touch
 * field type/reference behavior - purely cosmetic, so they run after
 * resolution here instead.
 */
export function useBillingUnitFormSchema() {
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const billingUnitCustomization = useMemo(() => {
    const customization = objectCustomizations?.data?.billingUnit;
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations]);

  const billingUnitFormSchema = useMemo(() => {
    const fields = billingUnitCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    return fieldsToSections(fields);
  }, [billingUnitCustomization]);

  const resolvedSchema = useResolveFormSchema(billingUnitFormSchema);

  return useMemo(() => processBillingUnitFormSchema(resolvedSchema), [resolvedSchema]);
}
